// DOM-facing helpers for the Osano blocking overlay. Kept separate from
// OsanoConsentOverlay.tsx so the inert/a11y/focus-trap bookkeeping can be
// unit tested directly against jsdom, without mounting the React component.
//
// None of these helpers ever call anything that writes Osano consent
// (`Osano.cm.setConsent`-style APIs don't exist here at all) — they only
// read SDK state and toggle presentation-only DOM attributes.

export type OsanoConsentValue = Record<string, string> | undefined;

export type OsanoCm = {
  jurisdiction?: unknown;
  gdprApplies?: unknown;
  dialogOpen?: boolean;
  drawerOpen?: boolean;
  showDialog?: () => void;
  hideDialog?: () => void;
  showDrawer?: () => void;
  showWidget?: () => void;
  hideWidget?: () => void;
  getConsent?: () => Record<string, string>;
};

export type OsanoUiComponent = 'dialog' | 'drawer' | 'widget';
export type OsanoUiState = 'show' | 'hide';

// `window.Osano` is the pre-load stub's callable queue function
// (`w[o] = w[o] || function () { ... }`), with `.cm` attached once the real
// SDK has loaded. Modeled as a callable with overloads for the three events
// this component subscribes to, per the JS API docs linked in the handover.
export type OsanoFn = {
  (
    event: 'onInitialized',
    callback: (consent: OsanoConsentValue) => void
  ): void;
  (
    event: 'onConsentSaved',
    callback: (consent: OsanoConsentValue) => void
  ): void;
  (
    event: 'onUiChanged',
    callback: (component: OsanoUiComponent, state: OsanoUiState) => void
  ): void;
  cm?: OsanoCm;
};

export type OsanoDomWindow = Window & {
  Osano?: OsanoFn;
};

export const OSANO_WINDOW_SELECTOR = '.osano-cm-window';
export const OSANO_DIALOG_SELECTOR = '.osano-cm-dialog';
export const OSANO_DIALOG_HIDDEN_CLASS = 'osano-cm-dialog--hidden';
export const OSANO_DRAWER_SELECTOR = '.osano-cm-info-dialog';

export const OSANO_CHOICE_REQUIRED_CLASS = 'osano-choice-required';
export const OSANO_BLOCKED_CLASS = 'osano-blocked';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'audio[controls]',
  'video[controls]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export const isElementVisible = (el: HTMLElement): boolean => {
  const style = window.getComputedStyle(el);
  // Deliberately not `el.offsetParent !== null`: Osano's dialog/window/drawer
  // are all `position: fixed`, and offsetParent is spec'd to be null for any
  // fixed-position element whose containing block is the viewport —
  // regardless of whether it's actually visible. offsetWidth/offsetHeight
  // still report real dimensions for fixed-position elements, and
  // getClientRects() is a fallback for the (rare) zero-size-but-rendered
  // case; both are 0/empty for `display: none` on the element or an
  // ancestor, which is what this check is actually trying to catch.
  return (
    style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    (el.offsetWidth > 0 ||
      el.offsetHeight > 0 ||
      el.getClientRects().length > 0)
  );
};

/**
 * The panel that must be made accessible-blocking while a choice is
 * required: the preferences drawer if it's open (visitor clicked "Manage
 * preferences"), otherwise the main dialog if it's open and not mid-hide
 * transition. Returns null if neither is currently a usable, visible panel
 * — callers must fail open in that case.
 */
export const getActivePanel = (): HTMLElement | null => {
  const drawer = document.querySelector<HTMLElement>(OSANO_DRAWER_SELECTOR);
  if (drawer && isElementVisible(drawer)) {
    return drawer;
  }

  const dialog = document.querySelector<HTMLElement>(OSANO_DIALOG_SELECTOR);
  if (
    dialog &&
    !dialog.classList.contains(OSANO_DIALOG_HIDDEN_CLASS) &&
    isElementVisible(dialog)
  ) {
    return dialog;
  }

  return null;
};

export const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
  ).filter(isElementVisible);

export const panelHasUsableControls = (panel: HTMLElement): boolean =>
  getFocusableElements(panel).length > 0;

/**
 * Makes everything along the panel's ancestor path, up to (not including)
 * `root`, inert except the panel's own branch. Never touches the panel's own
 * subtree, and never moves any SDK-owned DOM. Elements that were already
 * inert before this call are left alone and excluded from the returned
 * restore function, per the handover's "remove only the inert attributes we
 * added" requirement.
 */
export const applyInertToSiblings = (
  panel: HTMLElement,
  root: HTMLElement
): (() => void) => {
  const added: HTMLElement[] = [];
  let node: HTMLElement | null = panel;

  while (node && node !== root) {
    const parent: HTMLElement | null = node.parentElement;
    if (parent) {
      Array.from(parent.children).forEach((child) => {
        if (child === node || !(child instanceof HTMLElement)) return;
        if (child.hasAttribute('inert')) return;
        child.setAttribute('inert', '');
        added.push(child);
      });
    }
    node = parent;
  }

  return () => {
    added.forEach((el) => el.removeAttribute('inert'));
  };
};

/**
 * Sets role="dialog", aria-modal="true" and tabindex="-1" on the active
 * panel, remembering whatever was there before (including "wasn't set at
 * all") so it can be restored exactly on release.
 */
export const applyPanelA11yAttributes = (panel: HTMLElement): (() => void) => {
  const previous = {
    role: panel.getAttribute('role'),
    ariaModal: panel.getAttribute('aria-modal'),
    tabIndex: panel.getAttribute('tabindex'),
  };

  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('tabindex', '-1');

  const restoreAttr = (name: string, value: string | null) => {
    if (value === null) panel.removeAttribute(name);
    else panel.setAttribute(name, value);
  };

  return () => {
    restoreAttr('role', previous.role);
    restoreAttr('aria-modal', previous.ariaModal);
    restoreAttr('tabindex', previous.tabIndex);
  };
};

/**
 * Locks page scroll by setting `overflow: hidden` on `<html>`, remembering
 * the previous inline value *and* its CSS priority (e.g. `!important`) so
 * release restores the exact prior inline style rather than just clearing
 * it.
 */
export const lockHtmlOverflow = (): (() => void) => {
  const html = document.documentElement;
  const previousValue = html.style.getPropertyValue('overflow');
  const previousPriority = html.style.getPropertyPriority('overflow');

  html.style.setProperty('overflow', 'hidden');

  return () => {
    if (previousValue) {
      html.style.setProperty('overflow', previousValue, previousPriority);
    } else {
      html.style.removeProperty('overflow');
    }
  };
};

export type FocusTrapHandlers = {
  handleKeyDown: (event: KeyboardEvent) => void;
  handleFocusIn: (event: FocusEvent) => void;
};

/**
 * Builds the keydown/focusin handlers that keep focus inside whatever panel
 * `getPanel()` currently returns. Escape is swallowed entirely
 * (preventDefault + stopImmediatePropagation) so it can't dismiss the
 * required choice via any other document-level listener. Tab/Shift+Tab wrap
 * within the panel's focusable elements, and any focus that lands outside
 * the panel (e.g. a programmatic focus() elsewhere) is pulled back in.
 */
export const createFocusTrapHandlers = (
  getPanel: () => HTMLElement | null
): FocusTrapHandlers => {
  const handleKeyDown = (event: KeyboardEvent) => {
    const panel = getPanel();
    if (!panel) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements(panel);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first || !last) return;
    const active = document.activeElement;

    if (event.shiftKey) {
      if (active === first || !panel.contains(active)) {
        event.preventDefault();
        last.focus();
      }
    } else if (active === last || !panel.contains(active)) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleFocusIn = (event: FocusEvent) => {
    const panel = getPanel();
    if (!panel) return;

    const target = event.target;
    if (target instanceof Node && panel.contains(target)) return;

    const focusable = getFocusableElements(panel);
    (focusable[0] ?? panel).focus();
  };

  return { handleKeyDown, handleFocusIn };
};

/**
 * Enters the full accessible-blocking state for a single active panel:
 * inert siblings, dialog aria attributes, locked `<html>` overflow, a
 * document-level focus trap, and initial focus on the panel's first
 * control. Returns a single cleanup function that undoes exactly what this
 * call did and restores the previously focused element, per the handover's
 * "restore the previously focused element when releasing" requirement.
 *
 * Callers are responsible for only invoking this once per "becomes
 * blocked" transition, and calling the returned cleanup once per "stops
 * being blocked" transition (including on unmount) — this function itself
 * has no re-entrancy guard.
 */
export const enterBlockingForPanel = (
  panel: HTMLElement,
  root: HTMLElement
): (() => void) => {
  const previouslyFocused =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;

  const restoreInert = applyInertToSiblings(panel, root);
  const restoreA11y = applyPanelA11yAttributes(panel);
  const restoreOverflow = lockHtmlOverflow();

  const { handleKeyDown, handleFocusIn } = createFocusTrapHandlers(() => panel);
  // Capture phase so Escape is swallowed before any other document-level
  // listener (e.g. a modal elsewhere on the page) can react to it.
  document.addEventListener('keydown', handleKeyDown, true);
  document.addEventListener('focusin', handleFocusIn, true);

  const focusable = getFocusableElements(panel);
  (focusable[0] ?? panel).focus();

  return () => {
    document.removeEventListener('keydown', handleKeyDown, true);
    document.removeEventListener('focusin', handleFocusIn, true);
    restoreOverflow();
    restoreA11y();
    restoreInert();

    if (previouslyFocused && document.contains(previouslyFocused)) {
      previouslyFocused.focus();
    }
  };
};
